import { useState, useRef, useCallback } from "react";
import {
  Copy,
  Trash2,
  Pencil,
  Check,
  X,
  FileIcon,
  ImageIcon,
  VideoIcon,
  MusicIcon,
  FileTextIcon,
  ArchiveIcon,
  ExternalLink,
  CornerDownRight,
  FolderIcon,
  FolderOpenIcon,
  Download,
  ArrowRight,
  ChevronRight,
  ChevronDown,
  Loader2,
  RefreshCw,
} from "lucide-react";
import type { StorageEntry, FileEntry, FolderEntry } from "../lib/api";
import {
  formatBytes,
  formatDate,
  getFileUrl,
  getFolderDownloadUrl,
  getFileIcon,
  cn,
} from "../lib/utils";
import { useToast } from "./Toast";

interface FileRowProps {
  entry: StorageEntry;
  depth: number;
  isSelected: boolean;
  isExpanded: boolean;
  isLoading: boolean;
  draggedId: string | null;
  onSelect: (e: React.MouseEvent) => void;
  onRename: (id: string, newName: string) => void;
  onDelete: (id: string) => void;
  onNavigate: (folderId: string) => void;
  onToggle: () => void;
  onDragStart: (id: string) => void;
  onDragEnd: () => void;
  onDrop: (targetFolderId: string) => void;
  onReplace: (id: string, file: File) => void;
  onContextMenu: (e: React.MouseEvent, entry: StorageEntry) => void;
  onExternalDrop: (folderId: string, files: FileList) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  image: <ImageIcon className="w-3.5 h-3.5" />,
  video: <VideoIcon className="w-3.5 h-3.5" />,
  audio: <MusicIcon className="w-3.5 h-3.5" />,
  document: <FileTextIcon className="w-3.5 h-3.5" />,
  archive: <ArchiveIcon className="w-3.5 h-3.5" />,
  file: <FileIcon className="w-3.5 h-3.5" />,
};

export function FileRow({
  entry,
  depth,
  isSelected,
  isExpanded,
  isLoading,
  draggedId,
  onSelect,
  onRename,
  onDelete,
  onNavigate,
  onToggle,
  onDragStart,
  onDragEnd,
  onDrop,
  onReplace,
  onContextMenu: onCtxMenu,
  onExternalDrop,
}: FileRowProps) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState(entry.displayName);
  const [isDragOver, setIsDragOver] = useState(false);
  const [hoverPreview, setHoverPreview] = useState<{ x: number; y: number; src: string } | null>(null);
  const replaceInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const showPreview = useCallback((e: React.MouseEvent, src: string) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    setHoverPreview({ x: rect.right + 8, y: rect.top, src });
  }, []);
  const hidePreview = useCallback(() => setHoverPreview(null), []);

  const isFolder = entry.type === "folder";
  const file = isFolder ? null : (entry as FileEntry);
  const folder = isFolder ? (entry as FolderEntry) : null;

  const fileUrl = file ? getFileUrl(file.id, file.extension) : "";
  const folderUrl = folder ? getFolderDownloadUrl(folder.id) : "";
  const fileType = file ? getFileIcon(file.extension) : "folder";

  function handleCopy() {
    const url = isFolder ? folderUrl : fileUrl;
    navigator.clipboard.writeText(url);
    toast("LINK_COPIED", "success");
  }

  function handleRenameSubmit() {
    const trimmed = editValue.trim();
    if (trimmed && trimmed !== entry.displayName) {
      onRename(entry.id, trimmed);
      toast("RENAMED", "success");
    }
    setEditing(false);
  }

  function handleRenameCancel() {
    setEditValue(entry.displayName);
    setEditing(false);
  }

  function handleNameClick(e: React.MouseEvent) {
    e.stopPropagation();
    if (isFolder) {
      onToggle(); // Inline expand/collapse
    } else if (file) {
      window.open(fileUrl, "_blank");
    }
  }

  function handleChevronClick(e: React.MouseEvent) {
    e.stopPropagation();
    onToggle();
  }

  // --- Drag source ---
  function handleDragStartEvent(e: React.DragEvent) {
    e.dataTransfer.setData("text/plain", entry.id);
    e.dataTransfer.effectAllowed = "move";

    // Custom drag ghost — clean terminal label instead of blurry row capture
    const ghost = document.createElement("div");
    const typeLabel = isFolder
      ? "DIR"
      : (file?.extension?.toUpperCase() || "FILE");
    const typeColor = isFolder ? "#ffb74d" : "#a0a0a0";
    ghost.innerHTML = `<span style="color:${typeColor};margin-right:8px;font-weight:bold">[${typeLabel}]</span>${entry.displayName}`;
    Object.assign(ghost.style, {
      position: "fixed",
      top: "-1000px",
      left: "-1000px",
      background: "#1e1e1e",
      border: "1px solid #555",
      color: "#e0e0e0",
      fontFamily: "ui-monospace, monospace",
      fontSize: "11px",
      padding: "6px 12px",
      whiteSpace: "nowrap",
      boxShadow: "0 4px 12px rgba(0,0,0,0.5)",
      pointerEvents: "none",
      zIndex: "9999",
    });
    document.body.appendChild(ghost);
    e.dataTransfer.setDragImage(ghost, 0, 0);
    setTimeout(() => document.body.removeChild(ghost), 0);

    onDragStart(entry.id);
  }

  // --- Drop target (folders only) ---
  const folderDragCounter = useRef(0);

  function handleDragOver(e: React.DragEvent) {
    if (!isFolder || entry.id === draggedId) return;
    e.preventDefault();
    e.stopPropagation();
    const isExternal = e.dataTransfer.types.includes("Files");
    e.dataTransfer.dropEffect = isExternal ? "copy" : "move";
    setIsDragOver(true);
  }

  function handleDragEnter(e: React.DragEvent) {
    if (!isFolder || entry.id === draggedId) return;
    e.preventDefault();
    e.stopPropagation();
    folderDragCounter.current++;
    if (folderDragCounter.current === 1) setIsDragOver(true);
  }

  function handleDragLeave(e: React.DragEvent) {
    if (!isFolder) return;
    e.stopPropagation();
    folderDragCounter.current--;
    if (folderDragCounter.current === 0) setIsDragOver(false);
  }

  function handleDropEvent(e: React.DragEvent) {
    if (!isFolder) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    folderDragCounter.current = 0;

    // External file drop (from desktop)
    const isExternal = e.dataTransfer.types.includes("Files");
    if (isExternal && e.dataTransfer.files.length > 0) {
      onExternalDrop(entry.id, e.dataTransfer.files);
      return;
    }
    // Internal move
    onDrop(entry.id);
  }

  const dateStr = isFolder
    ? (entry as FolderEntry).createdAt
    : (entry as FileEntry).uploadedAt;

  // Dynamic left padding for tree depth
  const namePadLeft = 16 + depth * 24;

  return (
    <tr
      data-entry-id={entry.id}
      draggable={isSelected && !editing}
      onDragStart={handleDragStartEvent}
      onDragEnd={onDragEnd}
      onDragEnter={handleDragEnter}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDropEvent}
      onContextMenu={(e) => {
        e.preventDefault();
        e.stopPropagation();
        onCtxMenu(e, entry);
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(e);
      }}
      className={cn(
        "group border-b border-border-dim transition-colors font-mono text-xs h-9 cursor-default",
        draggedId === entry.id && "opacity-25",
        isSelected
          ? "bg-bg-panel border-l-2 border-l-text-primary"
          : isExpanded && isFolder
            ? "bg-bg-panel/50 border-l-2 border-l-warning"
            : "hover:bg-bg-surface border-l-2 border-l-transparent",
        isDragOver && "!bg-bg-surface ring-1 ring-inset ring-text-primary"
      )}
    >
      {/* Icon + Name */}
      <td
        className="py-1.5 align-middle border-r border-border-dim relative"
        style={{ paddingLeft: `${namePadLeft}px`, paddingRight: "16px" }}
      >
        {/* Depth guide lines */}
        {depth > 0 && (
          <div
            className="absolute top-0 bottom-0 pointer-events-none z-0"
            aria-hidden="true"
            style={{ left: "16px" }}
          >
            {Array.from({ length: depth }).map((_, i) => (
              <div
                key={i}
                className="absolute top-0 bottom-0 w-px bg-warning/30"
                style={{ left: `${i * 24 + 8}px` }}
              />
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 relative z-[1]">
          {/* Chevron (folders) / spacer (files) */}
          {isFolder ? (
            <button
              onClick={handleChevronClick}
              className="w-4 h-4 flex items-center justify-center shrink-0 text-text-muted hover:text-text-primary transition-colors"
            >
              {isLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : isExpanded ? (
                <ChevronDown className="w-3 h-3" />
              ) : (
                <ChevronRight className="w-3 h-3" />
              )}
            </button>
          ) : (
            <div className="w-4 h-4 shrink-0" />
          )}

          {/* Type icon / image thumbnail / folder icon */}
          {!isFolder && fileType === "image" && fileUrl ? (
            <div className="shrink-0">
              <img
                src={fileUrl}
                alt={entry.displayName}
                className="h-8 object-cover rounded-sm border border-border-dim cursor-pointer"
                loading="lazy"
                onMouseEnter={(e) => showPreview(e, fileUrl)}
                onMouseLeave={hidePreview}
              />
            </div>
          ) : folder && folder.iconId ? (
            <>
              <div className="shrink-0">
                <img
                  src={`/api/icons/${folder.iconId}`}
                  alt=""
                  className="h-5 object-cover rounded-sm cursor-pointer"
                  onMouseEnter={(e) => showPreview(e, `/api/icons/${folder.iconId}`)}
                  onMouseLeave={hidePreview}
                />
              </div>
              <div className="transition-colors shrink-0 text-warning">
                {isExpanded ? (
                  <FolderOpenIcon className="w-3.5 h-3.5" />
                ) : (
                  <FolderIcon className="w-3.5 h-3.5" />
                )}
              </div>
            </>
          ) : (
            <div
              className={cn(
                "transition-colors shrink-0",
                isFolder
                  ? "text-warning"
                  : isSelected
                    ? "text-text-primary"
                    : "text-text-muted group-hover:text-text-primary"
              )}
            >
              {isFolder ? (
                isExpanded ? (
                  <FolderOpenIcon className="w-3.5 h-3.5" />
                ) : (
                  <FolderIcon className="w-3.5 h-3.5" />
                )
              ) : (
                iconMap[fileType]
              )}
            </div>
          )}

          {/* Name */}
          <div className="min-w-0 flex-1">
            {editing ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleRenameSubmit();
                    if (e.key === "Escape") handleRenameCancel();
                  }}
                  className="px-2 py-0.5 bg-bg-base border border-text-primary text-text-primary w-full max-w-[200px] text-xs focus:outline-none rounded-none"
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameSubmit();
                  }}
                  className="p-0.5 text-success hover:bg-bg-panel border border-transparent hover:border-success transition-all"
                >
                  <Check className="w-3 h-3" />
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRenameCancel();
                  }}
                  className="p-0.5 text-error hover:bg-bg-panel border border-transparent hover:border-error transition-all"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            ) : (
              <div className="flex flex-col">
                <span
                  className={cn(
                    "font-bold truncate block max-w-xs cursor-pointer decoration-text-muted underline-offset-2",
                    isFolder
                      ? "text-warning hover:text-text-primary hover:underline"
                      : isSelected
                        ? "text-text-primary"
                        : "text-text-secondary group-hover:text-text-primary group-hover:underline"
                  )}
                  onClick={handleNameClick}
                >
                  {entry.displayName}
                </span>
                {file && file.displayName !== file.originalName && (
                  <span className="text-[10px] text-text-muted truncate flex items-center gap-1 opacity-70">
                    <CornerDownRight className="w-2.5 h-2.5" />{" "}
                    {file.originalName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </td>

      {/* Size */}
      <td className="px-4 py-1.5 text-text-secondary border-r border-border-dim font-mono text-[11px] tabular-nums">
        {file
          ? formatBytes(file.size)
          : folder?.size
            ? formatBytes(folder.size)
            : "\u2014"}
      </td>

      {/* Type badge */}
      <td className="px-4 py-1.5 border-r border-border-dim">
        <span
          className={cn(
            "inline-block px-1.5 py-0.5 text-[9px] font-bold uppercase bg-bg-panel tracking-wide border",
            isFolder
              ? "text-warning border-warning/30"
              : "text-text-secondary border-border-dim"
          )}
        >
          {isFolder ? "DIR" : file?.extension || "UNK"}
        </span>
      </td>

      {/* Date */}
      <td className="px-4 py-1.5 text-text-muted border-r border-border-dim text-[11px] tabular-nums font-mono">
        {formatDate(dateStr)}
      </td>

      {/* Actions */}
      <td className="px-4 py-1.5 text-right">
        <div className="flex items-center justify-end gap-1.5">
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleCopy();
            }}
            className="p-1 text-text-muted hover:text-text-primary hover:bg-bg-panel border border-transparent hover:border-text-muted transition-all rounded-none"
            title="COPY_LINK"
          >
            <Copy className="w-3.5 h-3.5" />
          </button>

          {isFolder ? (
            <>
              <a
                href={folderUrl}
                onClick={(e) => e.stopPropagation()}
                className="p-1 text-text-muted hover:text-text-primary hover:bg-bg-panel border border-transparent hover:border-text-muted transition-all rounded-none"
                title="DOWNLOAD_ZIP"
              >
                <Download className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onNavigate(entry.id);
                }}
                className="p-1 text-text-muted hover:text-text-primary hover:bg-bg-panel border border-transparent hover:border-text-muted transition-all rounded-none"
                title="ENTER_FOLDER"
              >
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </>
          ) : (
            <>
              <a
                href={fileUrl}
                target="_blank"
                rel="noopener"
                onClick={(e) => e.stopPropagation()}
                className="p-1 text-text-muted hover:text-text-primary hover:bg-bg-panel border border-transparent hover:border-text-muted transition-all rounded-none"
                title="OPEN"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  replaceInputRef.current?.click();
                }}
                className="p-1 text-text-muted hover:text-success hover:bg-bg-panel border border-transparent hover:border-success transition-all rounded-none"
                title="REPLACE_FILE"
              >
                <RefreshCw className="w-3.5 h-3.5" />
              </button>
              <input
                ref={replaceInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) onReplace(entry.id, f);
                  e.target.value = "";
                }}
              />
            </>
          )}

          <div className="w-[2px] h-4 bg-text-muted/60 mx-1.5 rounded-full" />

          <button
            onClick={(e) => {
              e.stopPropagation();
              setEditValue(entry.displayName);
              setEditing(true);
            }}
            className="p-1 text-text-muted hover:text-warning hover:bg-bg-panel border border-transparent hover:border-warning transition-all rounded-none"
            title="RENAME"
          >
            <Pencil className="w-3.5 h-3.5" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              onDelete(entry.id);
            }}
            className="p-1 text-text-muted hover:text-error hover:bg-bg-panel border border-transparent hover:border-error transition-all rounded-none"
            title="DELETE"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        </div>
      </td>

      {/* Fixed hover preview — renders outside table stacking context */}
      {hoverPreview && (
        <td className="p-0 border-0 w-0 h-0 overflow-visible">
          <div
            className="fixed pointer-events-none z-[99999]"
            style={{ left: hoverPreview.x, top: hoverPreview.y }}
          >
            <img
              src={hoverPreview.src}
              alt=""
              className="max-w-[280px] max-h-[200px] object-contain rounded border border-border-default shadow-[0_4px_20px_rgba(0,0,0,0.7)] bg-bg-panel"
            />
          </div>
        </td>
      )}
    </tr>
  );
}
